
import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter} from "react-router-dom";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider, 
  createHttpLink,
  split,
  } from "@apollo/client";
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import JWTManager from './utils/jwt.js'
import AuthContextProvider from './context/AuthContext';
import { offsetLimitPagination} from "@apollo/client/utilities";
import { HelmetProvider } from 'react-helmet-async';
import { MathJaxContext } from 'better-react-mathjax';

const mathJaxConfig = {
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['$$', '$$'], ['\\[', '\\]']],
    processEscapes: true,
  },
  options: {
    skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
  },
};
// Phần mềm chung gian gắn token vào mọi res đến máy chủ
const authLink = setContext((_, { headers }) => {
  // Lấy token trong biến của js 
  const token = JWTManager.getToken();
  
  return {
    // Gắn token vào headers của res
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  };
});

// Xử lý lỗi và tự động refresh token
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      // Kiểm tra nếu là lỗi authentication
      if (err.message.includes('Not authenticated') || 
          err.message.includes('jwt expired') ||
          err.extensions?.code === 'UNAUTHENTICATED') {
        
        // Thử refresh token
        return JWTManager.getRefreshToken().then(success => {
          if (success) {
            // Retry request với token mới
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${JWTManager.getToken()}`,
              },
            });
            return forward(operation);
          } else {
            // Refresh failed, có thể cần logout
            console.log('Token refresh failed');
          }
        }).catch(error => {
          console.error('Error during token refresh:', error);
        });
      }
    }
  }
  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const linkChain = createHttpLink({
  uri:process.env.REACT_APP_URL_WEB_SERVER,
  credentials: 'include'
});
const wsLink = new WebSocketLink({

  uri: process.env.REACT_APP_URL_WEB_SERVER_REALTIME,
  options: {
    reconnect: true,
    connectionParams: {
      authToken: process.env.REACT_APP_AUTH_TOKEN
    }
  }
});
const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  errorLink.concat(authLink.concat(linkChain))
);


const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          videosRecommended: offsetLimitPagination(),
          commentsVideo: offsetLimitPagination(),
          showVideoList: offsetLimitPagination(),
          videosLike: offsetLimitPagination(),
          videosWatched: offsetLimitPagination(),
          listVideoUser: offsetLimitPagination(
            ["idUser"], ["VideoList"], { 
              mergeMode: "merge",
              keyArgs: ["idUser"], // Sử dụng tham số "take" của truy vấn làm key
            }
          ),
          listVideoUserManage: offsetLimitPagination(
             ["VideoList"], { 
            mergeMode: "merge",
            keyArgs: false, // Sử dụng tham số "take" của truy vấn làm key
            merge(existing, incoming, { args: { offset = 0 }}) {
              // Slicing is necessary because the existing data is
              // immutable, and frozen in development.
              const merged = existing ? existing.slice(0) : [];
              for (let i = 0; i < incoming.length; ++i) {
                merged[offset + i] = incoming[i];
              }
              return merged;
            },
          }
          ),
          
        },
      },
    },
  }),
  link,
});

ReactDOM.render(
  <ApolloProvider client={client}>
        <React.StrictMode>
                <MathJaxContext config={mathJaxConfig} version={3} hideUntilTypeset="first">
                  <BrowserRouter>
                    <AuthContextProvider>
                      <HelmetProvider>
                        <App/>
                      </HelmetProvider>
                       
                    </AuthContextProvider>
                  </BrowserRouter>
                </MathJaxContext>
        </React.StrictMode>
  </ApolloProvider>,
  
  document.getElementById('root')
);


reportWebVitals();
